import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { useTranslation } from "react-i18next";
import {
  ArrowRight, ArrowUpRight, Gauge, Gift, Smile, TrendingUp, Quote,
  Building2, Landmark, Store, Stethoscope, ShieldCheck, HeartHandshake, Sparkles,
  LayoutDashboard, MessagesSquare, Globe2, Linkedin,
} from "lucide-react";
import { fetchProducts } from "../lib/api";
import ProductIcon from "../components/ProductIcon";
import { useDemo } from "../context/DemoContext";
import { JoynMark } from "../components/Logo";
import AIDashboardMock from "../components/AIDashboardMock";
import Seo from "../components/Seo";
import { PAGE_SEO, SITE } from "../lib/seo";

const helpIcons = [Gauge, Gift, Smile, TrendingUp];
const helpColors = ["#F05A28", "#F7931D", "#3AAA35", "#4BB5E7"];
const audienceIcons = [Building2, Landmark, Store, Stethoscope];
const audienceColors = ["#F05A28", "#4BB5E7", "#F7931D", "#3AAA35"];
const partnerIcons = [HeartHandshake, Sparkles, Gauge, ShieldCheck];
const partnerColors = ["#F05A28", "#F7931D", "#4BB5E7", "#3AAA35"];
const aiIcons = [Gauge, LayoutDashboard, MessagesSquare];
const aiColors = ["#F05A28", "#4BB5E7", "#3AAA35"];
const integrations = ["QuickBooks", "Zoom", "Sage", "PayPal", "Authorize.net", "AffiniPay", "Moneris", "SagePay", "SSO"];
const flags = ["us", "ca", "gb", "fr", "de", "au", "in", "br", "za", "ae", "sg", "jp"];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Home() {
  const { openDemo } = useDemo();
  const { t, i18n } = useTranslation();
  const [products, setProducts] = useState([]);
  useEffect(() => { fetchProducts().then(setProducts).catch(() => {}); }, [i18n.language]);
  const featured = products.slice(0, 6);

  const help = t("help.items", { returnObjects: true });
  const audiences = t("whoPreview.cards", { returnObjects: true });
  const partners = t("partner.items", { returnObjects: true });
  const aiFeatures = t("aiPreview.features", { returnObjects: true });
  const stats = t("global.stats", { returnObjects: true });
  const regions = t("global.regions", { returnObjects: true });

  return (
    <div data-testid="home-page">
      <Seo {...PAGE_SEO["/"]} path="/" />
      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-16 overflow-hidden">
        <div className="absolute top-24 -right-24 w-[560px] h-[560px] rounded-full opacity-20 blur-3xl anim-spin-slow"
          style={{ background: "conic-gradient(from 90deg, #F05A28, #F7931D, #D6DF23, #4BB5E7, #F05A28)" }} />
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-6">
            <motion.p custom={0} variants={fadeUp} initial="hidden" animate="show"
              className="inline-flex items-center gap-2 uppercase tracking-[0.2em] text-xs font-bold text-joyn-orange mb-6">
              <Sparkles size={14} /> {t("hero.badge")}
            </motion.p>
            <motion.h1 custom={1} variants={fadeUp} initial="hidden" animate="show"
              className="font-display font-extrabold text-5xl md:text-6xl lg:text-[4.2rem] tracking-tight leading-[0.98]">
              {t("hero.titleBefore")}<span className="joyn-gradient-text">joyn</span>{t("hero.titleAfter")}
            </motion.h1>
            <motion.p custom={2} variants={fadeUp} initial="hidden" animate="show" className="mt-4 text-xl font-semibold text-foreground/70">{t("hero.sub")}</motion.p>
            <motion.p custom={3} variants={fadeUp} initial="hidden" animate="show" className="mt-5 text-lg text-muted-foreground leading-relaxed max-w-xl">{t("hero.desc")}</motion.p>
            <motion.div custom={4} variants={fadeUp} initial="hidden" animate="show" className="mt-8 flex flex-wrap items-center gap-4">
              <button data-testid="hero-demo-btn" onClick={openDemo}
                className="group inline-flex items-center gap-2 bg-foreground text-white font-semibold px-8 py-4 rounded-full hover:-translate-y-1 transition-transform">
                {t("nav.demo")} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <Link to="/products" data-testid="hero-explore-btn"
                className="inline-flex items-center gap-2 border border-foreground/20 font-semibold px-8 py-4 rounded-full hover:bg-secondary transition-colors">
                {t("common.explore")}
              </Link>
            </motion.div>
          </div>

          <motion.div custom={2} variants={fadeUp} initial="hidden" animate="show" className="lg:col-span-6 relative">
            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl ring-1 ring-black/5">
              <img src="/brand/hero-conference.jpg" alt="Association professional at a conference" className="w-full h-[420px] md:h-[540px] object-cover object-center anim-kenburns" />
              <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(10,10,10,0) 55%, rgba(10,10,10,0.28))" }} />
              <div className="absolute inset-0 pointer-events-none rounded-[2rem]" style={{ boxShadow: "inset 0 0 0 6px rgba(255,255,255,0.35)" }} />
            </div>
            <div className="absolute -bottom-5 -left-3 md:-left-6 bg-white rounded-2xl shadow-xl px-6 py-4 border border-border anim-float">
              <p className="font-display font-extrabold text-3xl tracking-tight text-joyn-orange leading-none">360°</p>
              <p className="text-xs text-muted-foreground mt-1 max-w-[9rem]">{t("hero.stat1")}</p>
            </div>
            <div className="absolute -top-3 right-2 md:right-6 bg-foreground text-white rounded-2xl shadow-xl px-5 py-4 anim-float2">
              <p className="font-display font-extrabold text-xl tracking-tight leading-none">{t("hero.stat2Title")}</p>
              <p className="text-xs text-white/60 mt-1">{t("hero.stat2Sub")}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WE HELP */}
      <section className="border-y border-border bg-secondary/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-14">
          <p className="uppercase tracking-[0.25em] text-xs font-bold text-muted-foreground mb-8">{t("help.title")}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {help.map((h, i) => {
              const Icon = helpIcons[i];
              return (
                <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="flex items-center gap-4">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl text-white shrink-0" style={{ background: helpColors[i] }}><Icon size={22} /></span>
                  <p className="text-lg leading-tight"><span className="font-display font-extrabold">{h[0]}</span><br /><span className="text-muted-foreground">{h[1]}</span></p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* JOYN AI HIGHLIGHT */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-24">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div className="order-2 lg:order-1 pb-6"><AIDashboardMock /></div>
            <div className="order-1 lg:order-2">
              <span className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] mb-6">
                <Sparkles size={14} className="text-joyn-cyan" /> {t("aiPreview.badge")}
              </span>
              <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight leading-[1.02]">
                {t("aiPreview.titleBefore")}<span className="joyn-gradient-text">joyn AI</span>{t("aiPreview.titleAfter")}
              </h2>
              <p className="mt-5 text-lg text-muted-foreground leading-relaxed max-w-xl">{t("aiPreview.desc")}</p>
              <div className="mt-8 grid sm:grid-cols-3 gap-4">
                {aiFeatures.map((label, i) => {
                  const Icon = aiIcons[i];
                  return (
                    <div key={label} className="border border-border rounded-2xl p-4">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-white mb-3" style={{ background: aiColors[i] }}><Icon size={18} /></span>
                      <p className="font-display font-bold text-sm tracking-tight leading-snug">{label}</p>
                    </div>
                  );
                })}
              </div>
              <Link to="/ai" data-testid="home-ai-link" className="mt-8 group inline-flex items-center gap-2 bg-foreground text-white font-semibold px-8 py-4 rounded-full hover:-translate-y-1 transition-transform">
                {t("aiPreview.cta")} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCTS PREVIEW */}
      <section className="bg-secondary/40 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <p className="uppercase tracking-[0.25em] text-xs font-bold text-joyn-orange mb-4">{t("productsPreview.eyebrow")}</p>
              <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight leading-[1.02]">{t("productsPreview.title")}</h2>
              <p className="mt-4 text-lg text-muted-foreground">{t("productsPreview.desc")}</p>
            </div>
            <Link to="/products" data-testid="products-viewall-link" className="group inline-flex items-center gap-2 font-semibold hover:text-joyn-orange transition-colors shrink-0">
              {t("common.viewAll")} <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p, i) => (
              <motion.div key={p.slug} custom={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
                <Link to={`/products/${p.slug}`} data-testid={`product-card-${p.slug}`}
                  className="group block h-full bg-card border border-border rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-transform">
                  <div className="relative h-40 overflow-hidden bg-secondary">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <span className="absolute top-3 left-3 inline-flex h-10 w-10 items-center justify-center rounded-xl text-white shadow" style={{ background: p.accent }}><ProductIcon name={p.icon} size={20} /></span>
                  </div>
                  <div className="p-6">
                    <p className="uppercase tracking-[0.16em] text-[11px] font-bold mb-2" style={{ color: p.accent }}>{p.category}</p>
                    <h3 className="font-display font-bold text-xl tracking-tight mb-2">{p.name}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">{p.description}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold group-hover:text-joyn-orange transition-colors">{t("common.learnMore")} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" /></span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GLOBAL PRESENCE */}
      <section className="bg-foreground text-white relative overflow-hidden">
        <img src="/brand/world-map.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,10,10,0.7), rgba(10,10,10,0.85))" }} />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-24 md:py-28">
          <p className="inline-flex items-center gap-2 uppercase tracking-[0.25em] text-xs font-bold text-joyn-lime mb-5"><Globe2 size={15} /> {t("global.eyebrow")}</p>
          <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight leading-[1.03] max-w-3xl">{t("global.title")}</h2>
          <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-2xl">{t("global.desc")}</p>

          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
                <p className="font-display font-extrabold text-5xl md:text-6xl tracking-tight joyn-gradient-text">{s.value}</p>
                <p className="mt-2 text-sm text-white/70 max-w-[12rem]">{s.label}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-16">
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-white/50 mb-5">{t("global.regionsTitle")}</p>
            <div className="flex flex-wrap gap-2.5">
              {regions.map((r) => (
                <span key={r} className="text-sm font-semibold px-4 py-2 rounded-full bg-white/10 border border-white/15">{r}</span>
              ))}
            </div>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="global-flags">
            {flags.map((f) => (
              <img key={f} src={`https://flagcdn.com/w40/${f}.png`} alt={f} className="h-6 w-9 object-cover rounded-[3px] ring-1 ring-white/20" loading="lazy" />
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="bg-secondary/50 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <p className="uppercase tracking-[0.25em] text-xs font-bold text-joyn-cyan mb-4">{t("whoPreview.eyebrow")}</p>
              <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight">{t("whoPreview.title")}</h2>
            </div>
            <Link to="/who-we-serve" className="group inline-flex items-center gap-2 font-semibold hover:text-joyn-cyan transition-colors shrink-0">{t("common.learnMore")} <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" /></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {audiences.map((a, i) => {
              const Icon = audienceIcons[i];
              return (
                <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
                  className="bg-card border border-border rounded-2xl p-7 hover:-translate-y-1 hover:shadow-lg transition-transform">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl text-white mb-5" style={{ background: audienceColors[i] }}><Icon size={22} /></span>
                  <h3 className="font-display font-bold text-lg tracking-tight mb-2 leading-snug">{a.t}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{a.b}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PARTNER */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-24 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <p className="uppercase tracking-[0.25em] text-xs font-bold text-joyn-green mb-4">{t("partner.eyebrow")}</p>
          <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight leading-[1.03]">{t("partner.title")}</h2>
          <p className="mt-5 text-lg text-muted-foreground leading-relaxed">{t("partner.desc")}</p>
          <button onClick={openDemo} data-testid="partner-demo-btn" className="mt-8 inline-flex items-center gap-2 bg-foreground text-white font-semibold px-8 py-4 rounded-full hover:-translate-y-1 transition-transform">{t("common.talk")} <ArrowRight size={18} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          {partners.map((s, i) => {
            const Icon = partnerIcons[i];
            return (
              <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="border border-border rounded-2xl p-6">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl text-white mb-4" style={{ background: partnerColors[i] }}><Icon size={20} /></span>
                <h3 className="font-display font-bold tracking-tight mb-1.5">{s.t}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.b}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* INTEGRATIONS */}
      <section className="border-y border-border py-16 bg-secondary/40">
        <div className="max-w-7xl mx-auto px-4 md:px-8 mb-8">
          <p className="uppercase tracking-[0.25em] text-xs font-bold text-joyn-amber mb-2">{t("integrations.eyebrow")}</p>
          <h2 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight">{t("integrations.title")}</h2>
        </div>
        <Marquee speed={40} gradient gradientColor="#F7F9FB" gradientWidth={120} pauseOnHover>
          {integrations.map((name) => (<span key={name} className="mx-10 font-display font-bold text-2xl md:text-3xl tracking-tight text-foreground/25 hover:text-foreground transition-colors">{name}</span>))}
        </Marquee>
      </section>

      {/* LINKEDIN CTA */}
      <section className="border-y border-border bg-secondary/40">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div className="flex items-start sm:items-center gap-5">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl text-white shrink-0" style={{ background: "#0A66C2" }}><Linkedin size={26} /></span>
            <div>
              <p className="uppercase tracking-[0.2em] text-xs font-bold text-joyn-cyan mb-1.5">{t("social.eyebrow")}</p>
              <h2 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight leading-tight">{t("social.title")}</h2>
              <p className="mt-1.5 text-muted-foreground max-w-xl">{t("social.desc")}</p>
            </div>
          </div>
          <a href={SITE.linkedin} target="_blank" rel="noopener noreferrer" data-testid="home-linkedin-cta"
            className="group inline-flex items-center gap-2 bg-[#0A66C2] text-white font-semibold px-8 py-4 rounded-full hover:-translate-y-1 transition-transform shrink-0 self-start md:self-auto">
            <Linkedin size={18} /> {t("social.cta")}
          </a>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="bg-foreground text-white">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-24 md:py-28">
          <JoynMark size={44} onDark className="mb-8" />
          <Quote size={40} className="text-joyn-amber mb-6" />
          <blockquote className="font-display font-extrabold text-3xl md:text-4xl tracking-tight leading-[1.12]">“{t("testimonial.quote")}”</blockquote>
          <div className="mt-9 flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl flex items-center justify-center font-display font-extrabold text-white" style={{ background: "var(--joyn-orange)" }}>ED</div>
            <div>
              <p className="font-bold">{t("testimonial.name")}</p>
              <p className="text-sm text-white/60">{t("testimonial.role")}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
